(() => {
  if (customElements.get('product-info-drawer')) return;

  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  const LOCK_CLASS = 'product-info-drawer-lock';
  const TRANSITION_MS = 200;

  class ProductInfoDrawer extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-drawer-open]');
      const template = this.querySelector('template[data-drawer-template]');
      if (!this.trigger || !template) return;

      // The block sits inside the product form: render the drawer in <body> instead.
      // A copy left behind by a theme editor re-render is replaced.
      document.getElementById(this.dataset.drawerId)?.remove();
      this.drawer = template.content.firstElementChild.cloneNode(true);
      document.body.appendChild(this.drawer);
      this.panel = this.drawer.querySelector('[role="dialog"]');

      this.onTriggerClick = () => this.open();
      this.onDrawerClick = (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      };
      this.onKeydown = (event) => this.handleKeydown(event);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.drawer.addEventListener('click', this.onDrawerClick);
    }

    disconnectedCallback() {
      if (!this.drawer) return;
      this.close(false);
      this.trigger.removeEventListener('click', this.onTriggerClick);
      this.drawer.remove();
      this.drawer = null;
      this.unlockScroll();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.previousFocus = document.activeElement;
      clearTimeout(this.hideTimer);

      this.drawer.hidden = false;
      void this.drawer.offsetWidth; // start the slide-in transition from the closed state
      this.drawer.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add(LOCK_CLASS);
      document.addEventListener('keydown', this.onKeydown);
      this.panel.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);
      this.hideTimer = setTimeout(() => {
        if (!this.isOpen && this.drawer) this.drawer.hidden = true;
      }, TRANSITION_MS);
      this.unlockScroll();

      if (restoreFocus) (this.previousFocus || this.trigger).focus({ preventScroll: true });
    }

    unlockScroll() {
      if (!document.querySelector('.product-info-drawer__overlay.is-open')) {
        document.body.classList.remove(LOCK_CLASS);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('product-info-drawer', ProductInfoDrawer);

  // Custom Product Builder: its template hides the "Product pages" section, so the widget comes from
  // sections/product-info-drawer.liquid and is moved under the builder's action buttons. The builder
  // (React) renders late and can re-render its markup, so the slot is put back whenever it gets detached.
  const CPB_SOURCE = '[data-cpb-info-drawers]';
  const CPB_ANCHOR = '#product-builder .cpb-product-actions-container';
  let cpbSlot = null;

  function placeInCustomProductBuilder() {
    const anchor = document.querySelector(CPB_ANCHOR);
    if (!anchor) return;

    if (!cpbSlot) {
      cpbSlot = document.createElement('div');
      cpbSlot.className = 'product-info-drawer-cpb-slot';
    }
    if (anchor.nextElementSibling !== cpbSlot) anchor.after(cpbSlot);

    // New copies from the source (first load or theme editor re-render) replace the placed ones.
    document.querySelectorAll(`${CPB_SOURCE} product-info-drawer`).forEach((block) => {
      [...cpbSlot.children]
        .filter((placed) => placed.dataset.drawerId === block.dataset.drawerId)
        .forEach((placed) => placed.remove());
      cpbSlot.appendChild(block);
    });
  }

  if (document.querySelector(CPB_SOURCE) && document.getElementById('product-builder')) {
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        placeInCustomProductBuilder();
      });
    };
    schedule();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  }
})();
